import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import Calendar from "react-calendar";
import tw from "tailwind-styled-components";
import { GoChevronDown } from "react-icons/go";
import { FaRegCalendarAlt } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { useLocation } from "react-router-dom";

const DateFilter = ({ submitHandler, defaultLabel = "" }) => {
  const popupRef = useRef(null);
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";
  const [showPopup, setShowPopup] = useState(false);
  const [label, setLabel] = useState(defaultLabel || "Today");

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [rangeError, setRangeError] = useState("");

  const predefinedRanges = [
    {
      label: "Today",
      startDate: new Date(),
      endDate: new Date(),
    },
    {
      label: "Yesterday",
      startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      endDate: new Date(new Date().setDate(new Date().getDate() - 1)),
    },
    {
      label: "Last 7 Days",
      startDate: new Date(new Date().setDate(new Date().getDate() - 6)),
      endDate: new Date(),
    },
    {
      label: "This Month",
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
    },
    {
      label: "Last Month",
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth(), 0),
    },
    {
      label: "This Quarter",
      startDate: (() => {
        const currentQuarter = Math.floor(new Date().getMonth() / 3);
        return new Date(new Date().getFullYear(), currentQuarter * 3, 1);
      })(),
      endDate: new Date(),
    },
    {
      label: "Last Quarter",
      startDate: (() => {
        const currentQuarter = Math.floor(new Date().getMonth() / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const lastQuarterYear = currentQuarter === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();
        return new Date(lastQuarterYear, lastQuarter * 3, 1);
      })(),
      endDate: (() => {
        const currentQuarter = Math.floor(new Date().getMonth() / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const lastQuarterYear = currentQuarter === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear();
        return new Date(lastQuarterYear, (lastQuarter + 1) * 3, 0);
      })(),
    },
    {
      label: "This Financial Year",
      startDate: (() => {
        const currentMonth = new Date().getMonth();
        const financialYearStartMonth = 3;
        const currentYear = new Date().getFullYear();

        if (currentMonth >= financialYearStartMonth) {
          return new Date(currentYear, financialYearStartMonth, 1);
        } else {
          return new Date(currentYear - 1, financialYearStartMonth, 1);
        }
      })(),
      endDate: new Date(),
    },
    {
      label: "Last Financial Year",
      startDate: (() => {
        const currentMonth = new Date().getMonth();
        const financialYearStartMonth = 3;
        const currentYear = new Date().getFullYear();

        if (currentMonth >= financialYearStartMonth) {
          return new Date(currentYear - 1, financialYearStartMonth, 1);
        } else {
          return new Date(currentYear - 2, financialYearStartMonth, 1);
        }
      })(),
      endDate: (() => {
        const currentMonth = new Date().getMonth();
        const financialYearStartMonth = 3;
        const currentYear = new Date().getFullYear();

        if (currentMonth >= financialYearStartMonth) {
          return new Date(currentYear, financialYearStartMonth - 1, 31);
        } else {
          return new Date(currentYear - 1, financialYearStartMonth - 1, 31);
        }
      })(),
    },
  ];
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPopup]);

  const handleRangeSelect = (range) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    // setLabel(range.label);
    setLabel(`${format(range.startDate, "dd MMM yyyy")} - ${format(range.endDate, "dd MMM yyyy")}`);

    submitHandler({
      startDate: range.startDate,
      endDate: range.endDate,
    });

    setShowPopup(false);
  };

  const applyCustom = () => {
    if (!startDate || !endDate) {
      setRangeError("Please select both dates");
      return;
    }

    setLabel(`${format(startDate, "dd MMM yyyy")} - ${format(endDate, "dd MMM yyyy")}`);

    submitHandler({ startDate, endDate });
    setShowPopup(false);
  };
  const visibleRanges = isDashboard
    ? predefinedRanges.filter(
      (r) => !["Yesterday", "Last 7 Days"].includes(r.label)
    )
    : predefinedRanges;
  return (
    <Wrapper>
      <Button onClick={() => setShowPopup(true)}>
        <FaRegCalendarAlt className="h-4 w-4 mr-2 text-gray-500" />
        <LabelText>{label}</LabelText>
        <GoChevronDown className="h-5 w-5 ml-2 text-gray-500" />
      </Button>

      {showPopup && (
        <Overlay>
          <Popup ref={popupRef}>
            <PopupContent>
              <LeftPanel>
                <DateRangesContainer>
                  {visibleRanges.map((r) => (
                    <SidebarButton
                      key={r.label}
                      className={
                        label === r.label ? "bg-gray-200 font-semibold" : ""
                      }
                      onClick={() => handleRangeSelect(r)}
                    >
                      {r.label}
                    </SidebarButton>
                  ))}
                </DateRangesContainer>
                <ApplyButtonContainer >
                  <ApplyButton onClick={applyCustom}>Apply</ApplyButton>
                </ApplyButtonContainer>

              </LeftPanel>

              <RightPanel>
                <CalendarWrapper>
                  <Calendar
                    selectRange
                    onChange={(range) => {
                      setStartDate(range[0]);
                      setEndDate(range[1]);
                    }}
                    value={[startDate, endDate]}
                  />
                  <ApplyButtonContainer className="md:hidden block">
                    <ApplyButton onClick={applyCustom}>Apply</ApplyButton>
                  </ApplyButtonContainer>
                </CalendarWrapper>

                {rangeError && <ErrorText>{rangeError}</ErrorText>}
              </RightPanel>
            </PopupContent>
          </Popup>
        </Overlay>
      )}
    </Wrapper>
  );
};

const Wrapper = tw.div`flex text-left h-10 text-gray-700 `;
const Button = tw.button`
  text-gray-600 inline-flex items-center px-3 py-2 border border-gray-200 shadow-sm text-sm rounded-md bg-white hover:bg-gray-50 hover:border-gray-300
    min-w-[200px] md:w-[254px]
`;
const LabelText = tw.span`truncate max-w-[200px] sm:max-w-none`;
const Overlay = tw.div`
  fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50
  p-2 sm:p-4
`;
const Popup = tw.div`
  bg-white rounded-xl shadow-xl w-full max-w-[95%] sm:max-w-[90%] md:max-w-[600px] 
  h-auto lg:max-h-[55vh] max-h-[80vh]  overflow-hidden
`;
const PopupContent = tw.div`
  flex flex-col md:flex-row gap-4
`;
const DateRangesContainer = tw.div`
  flex-1 overflow-y-auto p-3 space-y-2
  lg:max-h-[calc(55vh-80px)] max-h-[calc(60vh-80px)]
`;
const ApplyButtonContainer = tw.div`
  p-3 border-t bg-white sticky bottom-0
`;
const LeftPanel = tw.div`
  w-full md:w-1/3 flex flex-col
  border-b md:border-b-0 md:border-r
  h-full hidden md:block
`;
const RightPanel = tw.div`
  w-full md:w-2/3 flex flex-col
  h-full
`;
const SidebarButton = tw.button`
  w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 text-sm sm:text-base
  transition-colors duration-200
`;
const ApplyButton = tw.button`
  w-full text-white py-2 rounded-md shadow-md bg-gradient-to-r from-primary-color to-teal-400  
  text-sm sm:text-base font-medium
`;
const ErrorText = tw.p`text-red-600 text-sm mt-2 text-center md:text-left`;

const CalendarWrapper = tw.div`
  [&_.react-calendar]:border-0
  [&_.react-calendar]:shadow-none
  [&_.react-calendar]:w-full

  [&_.react-calendar__navigation]:flex-wrap

  [&_.react-calendar__navigation__label]:text-base sm:text-lg
  [&_.react-calendar__navigation__label]:font-semibold

  [&_.react-calendar__tile]:text-xs sm:text-sm
  [&_.react-calendar__tile]:p-1 sm:p-2

  [&_.react-calendar__tile--active]:bg-gray-900
  [&_.react-calendar__tile--active]:text-white
  [&_.react-calendar__tile--active]:rounded-lg

  [&_.react-calendar__tile--rangeStart]:bg-gray-800
  [&_.react-calendar__tile--rangeStart]:text-white
  [&_.react-calendar__tile--rangeStart]:rounded-lg

  [&_.react-calendar__tile--rangeEnd]:bg-gray-800
  [&_.react-calendar__tile--rangeEnd]:text-white
  [&_.react-calendar__tile--rangeEnd]:rounded-lg

  [&_.react-calendar__tile--range]:bg-gray-200
  [&_.react-calendar__tile--range]:text-gray-900

  /* Mobile-specific adjustments */
  @media (max-width: 640px) {
    [&_.react-calendar__month-view__weekdays__weekday]:padding-1
    [&_.react-calendar__month-view__weekdays__weekday]:text-xs
  }
`;

export default DateFilter;